import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Handshake, Plus, Pencil, Trash2, Eye, EyeOff,
  ChevronUp, ChevronDown, Save, Upload, Image as ImageIcon
} from 'lucide-react';

const API = '/api';

const PARTNER_TYPES = [
  { value: 'strategic', label: 'Strategic Partner', label_ar: 'شريك استراتيجي' },
  { value: 'technology', label: 'Technology Partner', label_ar: 'شريك تقني' },
  { value: 'training', label: 'Training Partner', label_ar: 'شريك تدريب' },
  { value: 'consulting', label: 'Consulting Partner', label_ar: 'شريك استشاري' },
  { value: 'regional', label: 'Regional Partner', label_ar: 'شريك إقليمي' },
];

export const AdminCMSPartnersPage = () => {
  const { language } = useLanguage();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    logo_url: '',
    website_url: '',
    description: '',
    partner_type: 'strategic',
    is_active: true,
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/cms/partners`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPartners(data);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      setFormData({
        name: partner.name || '',
        name_ar: partner.name_ar || '',
        logo_url: partner.logo_url || '',
        website_url: partner.website_url || '',
        description: partner.description || '',
        partner_type: partner.partner_type || 'strategic',
        is_active: partner.is_active !== false,
      });
    } else {
      setEditingPartner(null);
      setFormData({
        name: '',
        name_ar: '',
        logo_url: '',
        website_url: '',
        description: '',
        partner_type: 'strategic',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await fetch(`${API}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: uploadFormData
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, logo_url: data.url }));
        toast.success('Logo uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Partner name is required');
      return;
    }

    try {
      const url = editingPartner 
        ? `${API}/admin/cms/partners/${editingPartner.partner_id}`
        : `${API}/admin/cms/partners`;
      
      const response = await fetch(url, {
        method: editingPartner ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingPartner ? 'Partner updated' : 'Partner added');
        setDialogOpen(false);
        fetchPartners();
      } else {
        toast.error('Failed to save partner');
      }
    } catch (error) {
      console.error('Error saving partner:', error);
      toast.error('Failed to save partner');
    }
  };

  const handleDelete = async () => {
    if (!partnerToDelete) return;

    try {
      const response = await fetch(`${API}/admin/cms/partners/${partnerToDelete.partner_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        toast.success('Partner deleted');
        setDeleteDialogOpen(false);
        setPartnerToDelete(null);
        fetchPartners();
      } else {
        toast.error('Failed to delete partner');
      }
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error('Failed to delete partner');
    }
  };

  const handleToggleActive = async (partner) => {
    try {
      const response = await fetch(`${API}/admin/cms/partners/${partner.partner_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ is_active: !partner.is_active })
      });

      if (response.ok) {
        toast.success(partner.is_active ? 'Partner hidden' : 'Partner visible');
        fetchPartners();
      }
    } catch (error) {
      console.error('Error toggling partner:', error);
    }
  };

  const handleMovePartner = async (partner, direction) => {
    const currentIndex = partners.findIndex(p => p.partner_id === partner.partner_id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= partners.length) return;

    const newPartners = [...partners];
    [newPartners[currentIndex], newPartners[newIndex]] = [newPartners[newIndex], newPartners[currentIndex]];
    
    const orders = newPartners.map((p, i) => ({ partner_id: p.partner_id, order: i + 1 }));

    try {
      const response = await fetch(`${API}/admin/cms/partners/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(orders)
      });

      if (response.ok) {
        fetchPartners();
      }
    } catch (error) {
      console.error('Error reordering:', error);
    }
  };

  const getPartnerTypeLabel = (type) => {
    const found = PARTNER_TYPES.find(t => t.value === type);
    return language === 'ar' ? (found?.label_ar || type) : (found?.label || type);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">
            {language === 'ar' ? 'إدارة الشركاء' : 'Partners Management'}
          </h1>
          <p className="text-[#6B7280] mt-1">
            {language === 'ar' 
              ? 'إدارة شعارات وأسماء الشركاء المعروضة على الموقع'
              : 'Manage partner logos and names displayed on the website'}
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-[#33AADD] hover:bg-[#2b8eb8]" data-testid="add-partner-btn">
          <Plus className="h-4 w-4 me-2" />
          {language === 'ar' ? 'إضافة شريك' : 'Add Partner'}
        </Button>
      </div>

      {/* Partners Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-8 text-[#6B7280]">Loading...</div>
        ) : partners.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-8 text-center text-[#6B7280]">
              No partners found. Add your first partner.
            </CardContent>
          </Card>
        ) : (
          partners.map((partner, index) => (
            <Card 
              key={partner.partner_id} 
              className={`transition-all ${!partner.is_active ? 'opacity-60 bg-gray-50' : ''}`}
              data-testid={`partner-card-${index}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center">
                  {/* Logo */}
                  <div className="h-20 w-32 mb-3 flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden">
                    {partner.logo_url ? (
                      <img 
                        src={partner.logo_url} 
                        alt={partner.name} 
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <Handshake className="h-10 w-10 text-gray-400" />
                    )}
                  </div>

                  {/* Name */}
                  <h3 className="font-semibold text-[#111827]">{partner.name}</h3>
                  {partner.name_ar && (
                    <p className="text-sm text-[#6B7280]" dir="rtl">{partner.name_ar}</p>
                  )}
                  
                  {/* Partner Type Badge */}
                  <Badge variant="outline" className="mt-2 text-xs">
                    {getPartnerTypeLabel(partner.partner_type)}
                  </Badge>

                  {/* Status Badge */}
                  {!partner.is_active && (
                    <Badge variant="secondary" className="mt-1 text-xs">Hidden</Badge>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-1 mt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMovePartner(partner, 'up')}
                      disabled={index === 0}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMovePartner(partner, 'down')}
                      disabled={index === partners.length - 1}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(partner)}
                      title={partner.is_active ? 'Hide' : 'Show'}
                    >
                      {partner.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(partner)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => {
                        setPartnerToDelete(partner);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPartner 
                ? (language === 'ar' ? 'تعديل الشريك' : 'Edit Partner')
                : (language === 'ar' ? 'إضافة شريك جديد' : 'Add New Partner')}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'أدخل اسم الشريك وشعاره'
                : 'Enter partner name and logo'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Logo Upload */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الشعار' : 'Logo'}</Label>
              <div className="flex items-center gap-4">
                <div className="h-16 w-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                  {formData.logo_url ? (
                    <img src={formData.logo_url} alt="Logo" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="partner-logo-upload"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('partner-logo-upload').click()}
                    disabled={uploading}
                  >
                    <Upload className="h-4 w-4 me-2" />
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                  <p className="text-xs text-[#6B7280] mt-1">Max 2MB, PNG or JPG</p>
                </div>
              </div>
            </div>

            {/* Partner Type */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'نوع الشراكة' : 'Partner Type'}</Label>
              <Select value={formData.partner_type} onValueChange={(v) => setFormData({...formData, partner_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PARTNER_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {language === 'ar' ? type.label_ar : type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'اسم الشريك *' : 'Partner Name *'}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., TechCorp International"
                data-testid="partner-name"
              />
            </div>

            {/* Arabic Name */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الاسم بالعربي' : 'Arabic Name'}</Label>
              <Input
                value={formData.name_ar}
                onChange={(e) => setFormData({...formData, name_ar: e.target.value})}
                placeholder="مثال: تك كورب الدولية"
                dir="rtl"
              />
            </div>

            {/* Website URL */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'رابط الموقع' : 'Website URL'}</Label>
              <Input
                value={formData.website_url}
                onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                placeholder="https://www.example.com"
                type="url"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'وصف (اختياري)' : 'Description (optional)'}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the partnership..."
                rows={2}
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>{language === 'ar' ? 'مرئي على الموقع' : 'Visible on Website'}</Label>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} className="bg-[#33AADD] hover:bg-[#2b8eb8]">
              <Save className="h-4 w-4 me-2" />
              {language === 'ar' ? 'حفظ' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'ar' ? 'حذف الشريك' : 'Delete Partner'}</DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? `هل أنت متأكد من حذف "${partnerToDelete?.name}"؟`
                : `Are you sure you want to delete "${partnerToDelete?.name}"?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 me-2" />
              {language === 'ar' ? 'حذف' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCMSPartnersPage;
